import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Grid,
  Alert,
  Snackbar,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  Chip,
  MenuItem,
  Checkbox,
  FormControlLabel,
  IconButton,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  FormControl,
  Select,
  InputLabel
} from '@mui/material';
import {
  MedicalServices,
  Save,
  Cancel,
  Delete,
  LocalHospital,
  Add,
  Edit,
  Print,
  Search,
  Clear,
  SwapVert
} from '@mui/icons-material';
import { useReactToPrint } from 'react-to-print';
import { visitAPI, consultationAPI, treatmentAPI } from '../services/api';
import VitalsDialog from './doctor/VitalsDialog';
import MedicalHistoryDialog from './doctor/MedicalHistoryDialog';
import OPDSlipPrint from './OPDSlipPrint';

function DoctorConsultation() {
  const [pendingVisits, setPendingVisits] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOrder, setSortOrder] = useState('newest');
  const [selectedVisit, setSelectedVisit] = useState(null);
  const [visitDetails, setVisitDetails] = useState(null);
  const [treatments, setTreatments] = useState([]);
  const [packages, setPackages] = useState([]);

  const [consultationData, setConsultationData] = useState({
    diagnosis: '',
    notes: '',
    suggestIpd: false,
    selectedTreatments: [],
    selectedPackages: []
  });

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  // Dialog states
  const [vitalsDialogOpen, setVitalsDialogOpen] = useState(false);
  const [medicalHistoryDialogOpen, setMedicalHistoryDialogOpen] = useState(false);

  // OPD Slip print state
  const [slipDialog, setSlipDialog] = useState(false);
  const [completedVisit, setCompletedVisit] = useState(null);
  const slipRef = useRef();
  const handlePrintSlip = useReactToPrint({ contentRef: slipRef, documentTitle: 'OPD_Slip' });

  useEffect(() => {
    loadPendingVisits();
    loadTreatments();
    loadPackages();
  }, []);

  const loadPendingVisits = async () => {
    try {
      const response = await visitAPI.getPending();
      setPendingVisits(response.data.data || response.data);
    } catch (err) {
      console.error('Failed to load pending visits:', err);
    }
  };

  const loadTreatments = async () => {
    try {
      const response = await treatmentAPI.getAll();
      setTreatments(response.data.data || response.data);
    } catch (err) {
      console.error('Failed to load treatments:', err);
    }
  };

  const loadPackages = async () => {
    try {
      const response = await treatmentAPI.getPackages();
      setPackages(response.data.data || response.data);
    } catch (err) {
      console.error('Failed to load packages:', err);
    }
  };

  const handleSelectVisit = async (visit) => {
    setSelectedVisit(visit);
    setError('');
    setSuccess('');

    try {
      const response = await visitAPI.getDetails(visit.id);
      setVisitDetails(response.data.data || response.data);
    } catch (err) {
      setError('Failed to load visit details.');
    }
  };

  const handleAddTreatment = (treatmentId) => {
    const treatment = treatments.find(t => t.id === parseInt(treatmentId));
    if (treatment && !consultationData.selectedTreatments.find(t => t.treatmentId === treatment.id)) {
      setConsultationData({
        ...consultationData,
        selectedTreatments: [
          ...consultationData.selectedTreatments,
          { treatmentId: treatment.id, treatmentName: treatment.name, quantity: 1, notes: '' }
        ]
      });
    }
  };

  const handleRemoveTreatment = (treatmentId) => {
    setConsultationData({
      ...consultationData,
      selectedTreatments: consultationData.selectedTreatments.filter(t => t.treatmentId !== treatmentId)
    });
  };

  const handleAddPackage = (packageId) => {
    const pkg = packages.find(p => p.id === parseInt(packageId));
    if (pkg && !consultationData.selectedPackages.find(p => p.packageId === pkg.id)) {
      setConsultationData({
        ...consultationData,
        selectedPackages: [
          ...consultationData.selectedPackages,
          { packageId: pkg.id, packageName: pkg.name, notes: '' }
        ]
      });
    }
  };

  const handleRemovePackage = (packageId) => {
    setConsultationData({
      ...consultationData,
      selectedPackages: consultationData.selectedPackages.filter(p => p.packageId !== packageId)
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await consultationAPI.create({
        visitId: selectedVisit.id,
        diagnosis: consultationData.diagnosis,
        notes: consultationData.notes,
        suggestIpd: consultationData.suggestIpd,
        treatments: consultationData.selectedTreatments,
        packages: consultationData.selectedPackages
      });

      setSuccess('Consultation completed successfully!');
      setCompletedVisit(selectedVisit);
      setSlipDialog(true);
    } catch (err) {
      setError('Failed to complete consultation. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveVitals = async (vitalsData) => {
    try {
      await visitAPI.updateVitals(selectedVisit.id, vitalsData);
      setSuccess('Vitals updated successfully!');
      // Reload visit details
      const response = await visitAPI.getDetails(selectedVisit.id);
      setVisitDetails(response.data.data || response.data);
    } catch (err) {
      throw err;
    }
  };

  const handleSaveMedicalHistory = async (historyData) => {
    try {
      await visitAPI.updateMedicalHistory(selectedVisit.id, historyData);
      setSuccess('Medical history updated successfully!');
      // Reload visit details
      const response = await visitAPI.getDetails(selectedVisit.id);
      setVisitDetails(response.data.data || response.data);
    } catch (err) {
      throw err;
    }
  };

  const filteredVisits = pendingVisits
    .filter((visit) => {
      if (!searchQuery.trim()) return true;
      const q = searchQuery.toLowerCase().trim();
      const fullName = `${visit.first_name || ''} ${visit.last_name || ''}`.toLowerCase();
      const code = (visit.patient_code || '').toLowerCase();
      const phone = (visit.phone || '').toLowerCase();
      const complaint = (visit.chief_complaint || '').toLowerCase();
      const opdId = (visit.opd_id || '').toString().toLowerCase();

      return (
        fullName.includes(q) ||
        code.includes(q) ||
        phone.includes(q) ||
        complaint.includes(q) ||
        opdId.includes(q)
      );
    })
    .sort((a, b) => {
      const idA = Number(a.id) || 0;
      const idB = Number(b.id) || 0;
      const dateA = new Date(a.visit_date || a.created_at || 0).getTime();
      const dateB = new Date(b.visit_date || b.created_at || 0).getTime();

      if (sortOrder === 'newest') {
        return dateB !== dateA ? dateB - dateA : idB - idA;
      } else {
        return dateA !== dateB ? dateA - dateB : idA - idB;
      }
    });

  return (
    <Card elevation={2}>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <MedicalServices sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
          <Typography variant="h5" component="h2" fontWeight="bold">
            Patient Consultation
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Snackbar
          open={!!success}
          autoHideDuration={6000}
          onClose={() => setSuccess('')}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert severity="success" onClose={() => setSuccess('')} sx={{ width: '100%' }}>
            {success}
          </Alert>
        </Snackbar>

        {!selectedVisit ? (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="h6" fontWeight="bold">
                  Pending Consultations
                </Typography>
                <Chip
                  label={`${filteredVisits.length} Patient${filteredVisits.length !== 1 ? 's' : ''}`}
                  color="primary"
                  size="small"
                />
              </Box>

              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', flexGrow: { xs: 1, sm: 0 } }}>
                <TextField
                  placeholder="Search by name, code, phone, complaint..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  size="small"
                  sx={{ minWidth: 280, flexGrow: 1 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search color="action" fontSize="small" />
                      </InputAdornment>
                    ),
                    endAdornment: searchQuery && (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setSearchQuery('')}>
                          <Clear fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />

                <FormControl size="small" sx={{ minWidth: 160 }}>
                  <InputLabel id="sort-order-label">Sort Order</InputLabel>
                  <Select
                    labelId="sort-order-label"
                    value={sortOrder}
                    label="Sort Order"
                    onChange={(e) => setSortOrder(e.target.value)}
                    startAdornment={
                      <InputAdornment position="start">
                        <SwapVert fontSize="small" color="action" />
                      </InputAdornment>
                    }
                  >
                    <MenuItem value="newest">Newest First</MenuItem>
                    <MenuItem value="oldest">Oldest First</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Box>

            {pendingVisits.length === 0 ? (
              <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'grey.50' }}>
                <Typography color="text.secondary">
                  No pending consultations
                </Typography>
              </Paper>
            ) : filteredVisits.length === 0 ? (
              <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'grey.50' }}>
                <Typography color="text.secondary">
                  No pending consultations match your search query "{searchQuery}"
                </Typography>
              </Paper>
            ) : (
              <TableContainer component={Paper} elevation={0} sx={{ border: 1, borderColor: 'divider' }}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <TableCell><strong>Patient Code</strong></TableCell>
                      <TableCell><strong>Name</strong></TableCell>
                      <TableCell><strong>Phone</strong></TableCell>
                      <TableCell><strong>Chief Complaint</strong></TableCell>
                      <TableCell><strong>Vitals</strong></TableCell>
                      <TableCell><strong>Visit Date</strong></TableCell>
                      <TableCell align="center"><strong>Action</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredVisits.map((visit) => (
                      <TableRow key={visit.id} hover>
                        <TableCell>
                          <Typography fontWeight="bold" variant="body2" color="primary">
                            {visit.patient_code}
                          </Typography>
                          {visit.opd_id && (
                            <Typography variant="caption" color="text.secondary">
                              OPD: {visit.opd_id}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography fontWeight="medium">
                            {visit.first_name} {visit.last_name}
                          </Typography>
                          {visit.gender && (
                            <Typography variant="caption" color="text.secondary">
                              {visit.gender}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>{visit.phone}</TableCell>
                        <TableCell>{visit.chief_complaint || 'N/A'}</TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            BP: {visit.blood_pressure || 'N/A'}<br />
                            Temp: {visit.temperature ? `${visit.temperature}°F` : 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {visit.visit_date ? new Date(visit.visit_date).toLocaleString('en-GB', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            }) : 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => handleSelectVisit(visit)}
                          >
                            Consult
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        ) : (
          <form onSubmit={handleSubmit}>
            {visitDetails && (
              <Paper sx={{ p: 3, mb: 3, bgcolor: 'grey.50' }} elevation={0}>
                <Typography variant="h6" gutterBottom color="primary">
                  <LocalHospital sx={{ verticalAlign: 'middle', mr: 1 }} />
                  Patient Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2" color="text.secondary">Name</Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {visitDetails.visit.first_name} {visitDetails.visit.last_name}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2" color="text.secondary">Patient Code</Typography>
                    <Typography variant="body1" fontWeight="medium">{visitDetails.visit.patient_code}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2" color="text.secondary">Phone</Typography>
                    <Typography variant="body1" fontWeight="medium">{visitDetails.visit.phone}</Typography>
                  </Grid>
                </Grid>

                <Divider sx={{ my: 2 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle1" fontWeight="bold">Vitals</Typography>
                  <Button
                    size="small"
                    startIcon={visitDetails.vitals ? <Edit /> : <Add />}
                    onClick={() => setVitalsDialogOpen(true)}
                  >
                    {visitDetails.vitals ? 'Edit' : 'Add'} Vitals
                  </Button>
                </Box>
                {visitDetails.vitals ? (
                  <Grid container spacing={2}>
                    <Grid item xs={6} sm={4} md={2}>
                      <Typography variant="body2" color="text.secondary">Blood Pressure</Typography>
                      <Typography variant="body1">{visitDetails.vitals.blood_pressure || 'N/A'}</Typography>
                    </Grid>
                    <Grid item xs={6} sm={4} md={2}>
                      <Typography variant="body2" color="text.secondary">Temperature</Typography>
                      <Typography variant="body1">{visitDetails.vitals.temperature ? `${visitDetails.vitals.temperature}°F` : 'N/A'}</Typography>
                    </Grid>
                    <Grid item xs={6} sm={4} md={2}>
                      <Typography variant="body2" color="text.secondary">Pulse Rate</Typography>
                      <Typography variant="body1">{visitDetails.vitals.pulse_rate ? `${visitDetails.vitals.pulse_rate} bpm` : 'N/A'}</Typography>
                    </Grid>
                    <Grid item xs={6} sm={4} md={2}>
                      <Typography variant="body2" color="text.secondary">Resp. Rate</Typography>
                      <Typography variant="body1">{visitDetails.vitals.respiratory_rate ? `${visitDetails.vitals.respiratory_rate}/min` : 'N/A'}</Typography>
                    </Grid>
                    <Grid item xs={6} sm={4} md={2}>
                      <Typography variant="body2" color="text.secondary">Weight</Typography>
                      <Typography variant="body1">{visitDetails.vitals.weight ? `${visitDetails.vitals.weight} kg` : 'N/A'}</Typography>
                    </Grid>
                    <Grid item xs={6} sm={4} md={2}>
                      <Typography variant="body2" color="text.secondary">Height</Typography>
                      <Typography variant="body1">{visitDetails.vitals.height ? `${visitDetails.vitals.height} cm` : 'N/A'}</Typography>
                    </Grid>
                    <Grid item xs={6} sm={4} md={2}>
                      <Typography variant="body2" color="text.secondary">BMI</Typography>
                      <Typography variant="body1">{visitDetails.vitals.bmi || 'N/A'}</Typography>
                    </Grid>
                    <Grid item xs={6} sm={4} md={2}>
                      <Typography variant="body2" color="text.secondary">O2 Saturation</Typography>
                      <Typography variant="body1">{visitDetails.vitals.oxygen_saturation ? `${visitDetails.vitals.oxygen_saturation}%` : 'N/A'}</Typography>
                    </Grid>
                    <Grid item xs={6} sm={4} md={2}>
                      <Typography variant="body2" color="text.secondary">Pain Scale</Typography>
                      <Typography variant="body1">{visitDetails.vitals.pain_scale !== null && visitDetails.vitals.pain_scale !== undefined ? `${visitDetails.vitals.pain_scale}/10` : 'N/A'}</Typography>
                    </Grid>
                  </Grid>
                ) : (
                  <Typography variant="body2" color="text.secondary">No vitals recorded yet</Typography>
                )}

                <Divider sx={{ my: 2 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle1" fontWeight="bold">Medical History</Typography>
                  <Button
                    size="small"
                    startIcon={visitDetails.medicalHistory ? <Edit /> : <Add />}
                    onClick={() => setMedicalHistoryDialogOpen(true)}
                  >
                    {visitDetails.medicalHistory ? 'Edit' : 'Add'} Medical History
                  </Button>
                </Box>
                {visitDetails.medicalHistory ? (
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary">Chief Complaint</Typography>
                      <Typography variant="body1">{visitDetails.medicalHistory.chief_complaint || 'N/A'}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">Past Medical History</Typography>
                      <Typography variant="body1">{visitDetails.medicalHistory.past_medical_history || 'N/A'}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">Allergies</Typography>
                      <Typography variant="body1">{visitDetails.medicalHistory.allergies || 'N/A'}</Typography>
                    </Grid>
                  </Grid>
                ) : (
                  <Typography variant="body2" color="text.secondary">No medical history recorded yet</Typography>
                )}
              </Paper>
            )}

            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Diagnosis *"
                  value={consultationData.diagnosis}
                  onChange={(e) => setConsultationData({ ...consultationData, diagnosis: e.target.value })}
                  required
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Notes"
                  value={consultationData.notes}
                  onChange={(e) => setConsultationData({ ...consultationData, notes: e.target.value })}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Prescribe Treatments
              </Typography>
              <TextField
                select
                fullWidth
                label="Select Treatment to Add"
                onChange={(e) => {
                  if (e.target.value) {
                    handleAddTreatment(e.target.value);
                  }
                }}
                value=""
              >
                <MenuItem value="">Select Treatment</MenuItem>
                {treatments.map(treatment => (
                  <MenuItem key={treatment.id} value={treatment.id}>
                    {treatment.name} - ₹{treatment.price} ({treatment.duration} min)
                  </MenuItem>
                ))}
              </TextField>

              {consultationData.selectedTreatments.length > 0 && (
                <Stack spacing={1} sx={{ mt: 2 }}>
                  {consultationData.selectedTreatments.map((treatment) => (
                    <Paper key={treatment.treatmentId} sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Typography sx={{ flex: 1 }}>{treatment.treatmentName}</Typography>
                      <IconButton
                        color="error"
                        size="small"
                        onClick={() => handleRemoveTreatment(treatment.treatmentId)}
                      >
                        <Delete />
                      </IconButton>
                    </Paper>
                  ))}
                </Stack>
              )}
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Prescribe Packages
              </Typography>
              <TextField
                select
                fullWidth
                label="Select Package to Add"
                onChange={(e) => {
                  if (e.target.value) {
                    handleAddPackage(e.target.value);
                  }
                }}
                value=""
              >
                <MenuItem value="">Select Package</MenuItem>
                {packages.map(pkg => (
                  <MenuItem key={pkg.id} value={pkg.id}>
                    {pkg.name} - ₹{pkg.price} ({pkg.duration} days)
                  </MenuItem>
                ))}
              </TextField>

              {consultationData.selectedPackages.length > 0 && (
                <Stack spacing={1} sx={{ mt: 2 }}>
                  {consultationData.selectedPackages.map((pkg) => (
                    <Paper key={pkg.packageId} sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Typography sx={{ flex: 1 }}>{pkg.packageName}</Typography>
                      <IconButton
                        color="error"
                        size="small"
                        onClick={() => handleRemovePackage(pkg.packageId)}
                      >
                        <Delete />
                      </IconButton>
                    </Paper>
                  ))}
                </Stack>
              )}
            </Box>

            <FormControlLabel
              control={
                <Checkbox
                  checked={consultationData.suggestIpd}
                  onChange={(e) => setConsultationData({ ...consultationData, suggestIpd: e.target.checked })}
                />
              }
              label="Suggest IPD Admission"
            />

            <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Save />}
                fullWidth
              >
                {loading ? 'Completing...' : 'Complete Consultation'}
              </Button>
              <Button
                type="button"
                variant="outlined"
                size="large"
                startIcon={<Cancel />}
                onClick={() => {
                  setSelectedVisit(null);
                  setVisitDetails(null);
                  setConsultationData({
                    diagnosis: '',
                    notes: '',
                    suggestIpd: false,
                    selectedTreatments: [],
                    selectedPackages: []
                  });
                }}
                sx={{ minWidth: 120 }}
              >
                Cancel
              </Button>
            </Box>
          </form>
        )}

        {/* Vitals Dialog */}
        {selectedVisit && (
          <VitalsDialog
            open={vitalsDialogOpen}
            onClose={() => setVitalsDialogOpen(false)}
            visitId={selectedVisit.id}
            existingVitals={visitDetails?.vitals}
            onSuccess={handleSaveVitals}
            onError={setError}
          />
        )}

        {/* Medical History Dialog */}
        {selectedVisit && (
          <MedicalHistoryDialog
            open={medicalHistoryDialogOpen}
            onClose={() => setMedicalHistoryDialogOpen(false)}
            visitId={selectedVisit.id}
            existingHistory={visitDetails?.medicalHistory}
            onSuccess={handleSaveMedicalHistory}
            onError={setError}
          />
        )}

        {/* OPD Slip Dialog — shown after consultation completion */}
        <Dialog open={slipDialog} onClose={() => {}} maxWidth="md" fullWidth>
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            Consultation Complete
            <Button variant="contained" startIcon={<Print />} onClick={handlePrintSlip}>
              Print OPD Slip
            </Button>
          </DialogTitle>
          <DialogContent dividers sx={{ p: 0, overflow: 'auto' }}>
            <div ref={slipRef}>
              {completedVisit && (
                <OPDSlipPrint
                  patient={{
                    firstName: completedVisit.first_name,
                    lastName: completedVisit.last_name,
                    patientCode: completedVisit.patient_code,
                    phone: completedVisit.phone,
                    dateOfBirth: completedVisit.date_of_birth,
                    gender: completedVisit.gender,
                    address: completedVisit.address,
                    chiefComplaint: completedVisit.chief_complaint
                  }}
                  visitInfo={{ visitType: completedVisit.visit_type, opdId: completedVisit.opd_id || '' }}
                  vitals={visitDetails?.vitals}
                  consultationInfo={{
                    doctorName: JSON.parse(localStorage.getItem('user') || '{}').full_name || '',
                    diagnosis: consultationData.diagnosis,
                    notes: consultationData.notes,
                    treatments: consultationData.selectedTreatments,
                    packages: consultationData.selectedPackages
                  }}
                />
              )}
            </div>
          </DialogContent>
          <DialogActions>
            <Button
              variant="outlined"
              onClick={() => {
                setSlipDialog(false);
                setCompletedVisit(null);
                setSelectedVisit(null);
                setVisitDetails(null);
                setConsultationData({ diagnosis: '', notes: '', suggestIpd: false, selectedTreatments: [], selectedPackages: [] });
                setSuccess('');
                loadPendingVisits();
              }}
            >
              Close & Next Patient
            </Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  );
}

export default DoctorConsultation;
